/**
 * API URLs for the admin panel.
 *
 * - `PROD_BASE` — primary Nest API (birdnest).
 * - `DEV_BASE` — legacy/dev origin (devde); for local testing against dev.
 * - `LOCAL_BASE` — locally running Nest API.
 * - `FASTAPI_BASE` — keyword extraction and related FastAPI services.
 * - Or replace any `endpoints.*` value with a full `https://...` URL.
 */

export const PROD_BASE = "https://birdnest.muns.io";
export const DEV_BASE = "https://devde.muns.io";
/** Local Nest API. */
export const LOCAL_BASE = "http://localhost:5003";
/** FastAPI services (keyword extraction, etc.). */
export const FASTAPI_BASE = "https://fastapi.muns.io";
/**
 * Self-hosted Infisical instance. All Infisical traffic is sent here directly from the browser.
 * Keep this separate from the backend endpoint that only supplies the encrypted machine identity.
 */
export const INFISICAL_BASE = "https://config.dev.muns.io";

/** Munshot Actions API. */
export const MUNSHOT_ACTIONS_BASE = PROD_BASE;

/**
 * Origin for every `/email/forwarding-accounts` call (list, add, edit, remove) used by
 * both Broker Dash Operations tabs. Point at `DEV_BASE` to test backend changes that are
 * not yet deployed to production.
 */
export const FORWARDING_BASE = PROD_BASE;

/** Primary API origin for agent/dashboard calls. */
export const API_BASE_URL = PROD_BASE;

/**
 * Origin for dashboard create/update/refresh/admin-list calls.
 *
 * Point this at `DEV_BASE` (or `LOCAL_BASE` for a locally running Nest API)
 * when testing backend changes that are not yet deployed to production.
 */
export const DASHBOARDS_BASE = PROD_BASE;

/**
 * Named endpoints (all used by the app).
 * Each value may be `${PROD_BASE}/path`, `${FASTAPI_BASE}/path`, or a full URL string.
 */
export const endpoints = {
  /** POST — Google credential login */
  authLogin: `${PROD_BASE}/auth/login`,
  /** POST — register user to org */
  authRegister: `${PROD_BASE}/auth/register`,
  createUser: `${PROD_BASE}/create/user`,
  /** GET/POST — user auth status */
  authStatus: `${PROD_BASE}/auth/status`,
  /** PATCH — update user_type (`{ userId, user_type }`) */
  authUserType: `${PROD_BASE}/auth/user-type`,
  /** PATCH — hide/show own org's dashboards for a user (`{ userId, hide_org_dashboards }`) */
  authHideOrgDashboards: `${DEV_BASE}/auth/hide-org-dashboards`,

  /** GET — list organisations (dashboards admin) */
  dashboardsOrgs: `${PROD_BASE}/orgs`,
  /** GET — org users for dashboard visibility picker */
  dashboardsOrgUsers: `${PROD_BASE}/orgs/users`,

  /** GET — list organisations */
  orgs: `${PROD_BASE}/orgs`,
  /** GET — org users (admin) */
  orgsUsers: `${PROD_BASE}/orgs/users`,

  /** POST — multipart org icon */
  storageUploadIcon: `${PROD_BASE}/storage/upload_icon`,

  /** PATCH — move a user to a different organization (`{ userId, organizationId }`) */
  adminMoveUserOrg: `${PROD_BASE}/admin/users/organization`,

  /** PATCH — upsert an org subscription (`{ organizationId, grace_period_days, is_active, devices_per_user, … }`) */
  adminSubscriptions: `${PROD_BASE}/admin/subscriptions`,
  /** POST — record an invoice for an org (`{ organizationId, months_paid, … }`) */
  adminInvoices: `${PROD_BASE}/admin/invoices`,

  /** GET — obtain the encrypted Infisical machine identity and its configured scope. */
  adminCredentials: `${PROD_BASE}/secrets/infisical`,
  /** POST — synchronize secrets for dashboards. */
  secretsSync: `${DEV_BASE}/secrets/sync`,

  /** GET — agent categories; PATCH — rename category */
  agentsCategories: `${PROD_BASE}/agents/categories`,
  /** GET — form types for agents */
  agentsForms: `${PROD_BASE}/agents/forms`,

  /** POST — search/get all stock tickers */
  stockSearch: `${PROD_BASE}/stock/search`,
  /** GET — sector / industry list for category template defaults */
  stockSectorIndustryList: `${PROD_BASE}/stock/sector-industry-list`,

  /** POST — create library agent */
  agentsLibrary: `${PROD_BASE}/agents/library`,
  /** GET — library admin list (cursor pagination) */
  agentsLibraryAdmin: `${PROD_BASE}/agents/library-admin`,
  /** POST — edit library agent details */
  agentsEditAgentDetails: `${PROD_BASE}/agents/edit_agent_details`,
  /** DELETE — body `{ agentId }` */
  agentsLibraryDelete: `${PROD_BASE}/agents/library`,

  /** POST — create dashboard */
  agentsDashboards: `${DASHBOARDS_BASE}/agents/dashboards`,
  /** GET admin list (`?format=json&page=&limit=&search=&category=&tickers=&favourite=&isCorporateAssigned=`) */
  agentsDashboardsAdmin: `${DASHBOARDS_BASE}/agents/dashboards-admin`,
  /** POST — ask dashboard agent (async job) */
  agentsDashboardAsk: `${PROD_BASE}/agents/dashboard/ask`,
  /** POST — extract keywords for an existing dashboard (`{ dashboard_id }`) — fastapi */
  dashboardExtractKeywords: `${FASTAPI_BASE}/dashboard/extract-keywords`,

  /** GET — list sheet templates (published, org-visible) */
  sheetsTemplates: `${PROD_BASE}/sheets/templates`,
  /** POST — promote an existing sheet to a template (admin) */
  sheetsPromoteToTemplate: `${PROD_BASE}/sheets/promote-to-template`,

  /**
   * GET — list a user's forwarding accounts (`?user_index=`)
   * POST — share a forwarding account with users (`{ account_email, user_indexes?, user_emails?, label? }`, admin only;
   *   at least one of user_indexes / user_emails). Returns `{ results: [{ user_index, status, account?, reason? }], not_found }`
   */
  emailForwardingAccounts: `${FORWARDING_BASE}/email/forwarding-accounts`,

  /** GET / POST — research DAG predefined templates (admin) */
  researchDagTemplates: `${FASTAPI_BASE}/research-dag/admin/templates`,
  /** POST — research DAG planner agent; streams `application/x-ndjson` (stateless, send full history) */
  researchDagPlanner: `${FASTAPI_BASE}/research-dag/planner`,
  /** POST — start a template run in the background; returns `{ run_id }` in ~50ms */
  researchDagRunTemplateStream: `${FASTAPI_BASE}/research-dag/run-template-stream`,
  /** GET — the user's past runs (id, status, query, timings, synthesis preview) */
  researchDagRuns: `${FASTAPI_BASE}/research-dag/runs`,

  /** GET / POST — configured GitHub accounts (PAT is write-only). */
  githubAccounts: `${MUNSHOT_ACTIONS_BASE}/admin/github`,
  /** GET — repositories available to a configured GitHub account. */
  githubRepositories: `${MUNSHOT_ACTIONS_BASE}/admin/github/repositories`,
  /** GET / POST — reusable Munshot Actions workflow templates. */
  githubWorkflowTemplates: `${MUNSHOT_ACTIONS_BASE}/admin/workflows`,
  /** GET — Munshot Actions execution history. */
  githubWorkflowRuns: `${MUNSHOT_ACTIONS_BASE}/admin/workflow-runs`,
  /** GET / POST — recurring Munshot workflow schedules. */
  workflowSchedules: `${MUNSHOT_ACTIONS_BASE}/admin/workflow-schedules`,
  /** GET — registered Munshot self-hosted runners. */
  munshotRunners: `${MUNSHOT_ACTIONS_BASE}/admin/munshot-actions/runners`,
};

/** GET — poll dashboard-ask job by id */
export function urlAgentsDashboardAskJob(jobId) {
  return `${PROD_BASE}/agents/dashboard/ask/${jobId}`;
}

/** GET — single library agent (admin details) */
export function urlAgentLibraryAdminDetails(agentLibraryId) {
  return `${PROD_BASE}/agents/agent-library-admin-details/${agentLibraryId}`;
}

/** PATCH / DELETE — dashboard by id */
export function urlAgentsDashboard(dashboardId) {
  return `${DASHBOARDS_BASE}/agents/dashboards/${dashboardId}`;
}

/**
 * POST — mark a dashboard as refreshed.
 * Body is optional: `{ refreshFrequencyHours?, lastRefreshedAt?, nextRefreshAt? }`
 * (snake_case aliases accepted); the backend fills in whatever is missing.
 */
export function urlAdminRefreshDashboard(dashboardId) {
  return `${DASHBOARDS_BASE}/agents/dashboards/${dashboardId}/refresh`;
}

/** GET / DELETE — admin fallback thumbnail */
export function urlAgentsDashboardAdminThumbnail(dashboardId) {
  return `${PROD_BASE}/agents/dashboards/${dashboardId}/thumbnail/admin`;
}

/** POST — multipart admin fallback thumbnail upload */
export function urlAgentsDashboardAdminThumbnailUpload(dashboardId) {
  return `${PROD_BASE}/agents/dashboards/${dashboardId}/thumbnail/admin-upload`;
}

/** GET — sheet template details; DELETE — remove template (admin) */
export function urlSheetsTemplate(templateId) {
  return `${PROD_BASE}/sheets/templates/${templateId}`;
}

/** GET (SSE) — live event stream for one research-DAG run; not replayed, so connect immediately */
export function urlResearchDagRunStream(runId) {
  return `${FASTAPI_BASE}/research-dag/runs/${runId}/stream`;
}

/** GET — one node's stored answer/sources/status, for a past run or after a reconnect */
export function urlResearchDagRunNodeOutput(runId, nodeId) {
  return `${FASTAPI_BASE}/research-dag/run-node-output?run_id=${encodeURIComponent(runId)}&node_id=${encodeURIComponent(nodeId)}`;
}

/** PUT / DELETE — research DAG template by id */
export function urlResearchDagTemplate(templateId) {
  return `${FASTAPI_BASE}/research-dag/admin/templates/${templateId}`;
}

/**
 * PATCH — update a forwarding account label (`{ user_index, label? }`, admin only)
 * DELETE — remove a forwarding account (`?user_index=`, admin only)
 */
export function urlEmailForwardingAccount(accountId) {
  return `${FORWARDING_BASE}/email/forwarding-accounts/${accountId}`;
}

/**
 * Secret names and values are not proxied through the backend: the panel calls Infisical's v4
 * secrets API itself at `INFISICAL_BASE` — see `src/utils/infisicalClient.js`.
 */

/** GET — organization subscription, status and invoice history (admin) */
export function urlAdminOrgSubscription(organizationId) {
  return `${PROD_BASE}/admin/subscriptions/${organizationId}`;
}

export function urlGithubAccount(accountId) {
  return `${endpoints.githubAccounts}/${encodeURIComponent(accountId)}`;
}

export function urlGithubBranches(owner, repo, accountId) {
  return `${endpoints.githubRepositories}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches?accountId=${encodeURIComponent(accountId)}`;
}

export function urlGithubWorkflowTemplate(templateId) {
  return `${endpoints.githubWorkflowTemplates}/${encodeURIComponent(templateId)}`;
}

export function urlGithubWorkflowRun(templateId) {
  return `${urlGithubWorkflowTemplate(templateId)}/run`;
}

export function urlGithubExecution(executionId) {
  return `${endpoints.githubWorkflowRuns}/${encodeURIComponent(executionId)}`;
}

export function urlCancelMunshotExecution(executionId) {
  return `${urlGithubExecution(executionId)}/cancel`;
}

export function urlMunshotArtifact(executionId, artifactId) {
  return `${urlGithubExecution(executionId)}/artifacts/${encodeURIComponent(artifactId)}`;
}

export function urlWorkflowSchedule(scheduleId) {
  return `${endpoints.workflowSchedules}/${encodeURIComponent(scheduleId)}`;
}

/**
 * POST — multipart (`file`: .xlsx/.csv) upload to an organization's shared Client Finder company list (admin only).
 * FastAPI: fastapi-server `api/endpoints/client_finder.py`.
 */
export function urlClientFinderOrgCompaniesUpload(orgId) {
  return `${FASTAPI_BASE}/client-finder/admin/orgs/${encodeURIComponent(orgId)}/companies/upload`;
}
